import * as React from "react";

import { cn } from "@/common/helpers/utils";
import { Button, ButtonColorVariant, ButtonVariant } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader, LoaderSize, LoaderVariant } from "@/components/ui/loader";

interface FormCardProps extends Omit<React.ComponentProps<typeof Card>, "children"> {
  children: React.ReactNode;
  contentClassName?: string;
  description?: React.ReactNode;
  footer?: React.ReactNode;
  footerClassName?: string;
  headerClassName?: string;
  title: string;
  titleHidden?: boolean;
}

function FormCard({
  children,
  className,
  contentClassName,
  description,
  footer,
  footerClassName,
  headerClassName,
  title,
  titleHidden = false,
  ...props
}: FormCardProps) {
  return (
    <Card className={className} {...props}>
      {titleHidden ? (
        <CardTitle className="sr-only">{title}</CardTitle>
      ) : (
        <CardHeader className={headerClassName}>
          <CardTitle>{title}</CardTitle>
          {description ? <CardDescription>{description}</CardDescription> : null}
        </CardHeader>
      )}
      <CardContent className={contentClassName}>{children}</CardContent>
      {footer ? <CardFooter className={footerClassName}>{footer}</CardFooter> : null}
    </Card>
  );
}

interface FormCardActionsProps extends React.ComponentProps<"div"> {
  cancelLabel: React.ReactNode;
  cancelVariant?: ButtonVariant;
  isPending?: boolean;
  onCancel: () => void;
  submitDisabled?: boolean;
  submitLabel: React.ReactNode;
}

interface FormCardSectionProps extends Omit<React.ComponentProps<"fieldset">, "children" | "title"> {
  children: React.ReactNode;
  contentClassName?: string;
  description?: React.ReactNode;
  descriptionClassName?: string;
  title: React.ReactNode;
  titleClassName?: string;
}

function FormCardActions({
  cancelLabel,
  cancelVariant = ButtonVariant.Outlined,
  className,
  isPending = false,
  onCancel,
  submitDisabled = false,
  submitLabel,
  ...props
}: FormCardActionsProps) {
  return (
    <div className={cn("flex flex-wrap justify-end gap-4", className)} {...props}>
      <Button
        type="button"
        variant={cancelVariant}
        colorVariant={ButtonColorVariant.Secondary}
        onClick={onCancel}
        disabled={isPending}
      >
        {cancelLabel}
      </Button>
      <Button type="submit" disabled={submitDisabled || isPending}>
        {isPending ? (
          <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} inheritColor className="mr-2" />
        ) : null}
        {submitLabel}
      </Button>
    </div>
  );
}

function FormCardSection({
  children,
  className,
  contentClassName,
  description,
  descriptionClassName,
  title,
  titleClassName,
  "aria-describedby": ariaDescribedBy,
  ...props
}: FormCardSectionProps) {
  const descriptionId = React.useId();
  const mergedAriaDescribedBy =
    [ariaDescribedBy, description ? descriptionId : undefined].filter(Boolean).join(" ") || undefined;

  return (
    <fieldset
      className={cn("bg-background/20 min-w-0 overflow-hidden rounded-lg border p-0", className)}
      aria-describedby={mergedAriaDescribedBy}
      {...props}
    >
      <legend className="sr-only">{title}</legend>
      <div className="bg-background/30 border-b px-4 py-2.5">
        <div className={cn("text-foreground text-sm font-semibold", titleClassName)}>{title}</div>
        {description ? (
          <div id={descriptionId} className={cn("text-muted-foreground mt-1 text-sm", descriptionClassName)}>
            {description}
          </div>
        ) : null}
      </div>
      <div className={cn("px-4 pt-3 pb-4", !contentClassName && "space-y-3", contentClassName)}>{children}</div>
    </fieldset>
  );
}

export { FormCard, FormCardActions, FormCardSection };
